import React, { memo, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IconButton } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';

import Button from '../../../common/components/Button/Button';
import { isOnlyMobile } from '../../../common/services/isMobile';
import { Toolbar } from '../../../common/components/Toolbar/Toolbar';
import AdvancedSearch from '../../../common/components/AdvancedSearch/AdvancedSearch';
import { getChargersAdvancedSearchConfig } from '../constants';
import PersonalizeModal from './PersonalizeModal/component';
import { chargersInfoTableId } from './ChargersList';
import MappedLabel from '../../../common/components/MappedLabel/MappedLabel';
import { getShowTechnicalInfoFieldsFlag } from '../../../common/services/AuthenticationRules';
import TransferButton from '../../../common/components/TransferButton';
import { DetailsUpdateModal } from './DetailsUpdateModal/DetailsUpdateModal';
import { selectUserPermissions } from '../../Login/selectors';
import hasAccess, { editChargerDetailsAccessId, viewChargerCustomer } from '../../../common/services/HasAccess';

const columnsConfigButtonStyle = { padding: '12px 0px' };

const DETAILS_UPDATE_BUTTON_TITLE = 'Details Update';

export const getAdvancedSearchExcludedFields = (config) => {
  const fields = ['user_name', 'current_firmware_version', 'new_firmware_version', 'details', 'request_time', 'end_time', 'f_status'];

  if (!config || !getShowTechnicalInfoFieldsFlag(config)) {
    fields.push('enclosure', 'product_level', 'product_version');
  }

  return fields;
};

const ChargersListToolbar = memo(
  ({
    togglePersonalizeModal,
    setColumnsConfig,
    columnsConfig,
    config,
    personalizeModal,
    isShowAdvancedSearch,
    advancedSearchEnabled,
    onFilterApply,
    onToggleAdvancedSearch,
    selectedDataIds,
    selectAll,
  }) => {
    const [isOpenDetailsUpdateModal, setIsOpenDetailsUpdateModal] = useState(false);

    const excludedFields = useMemo(() => getAdvancedSearchExcludedFields(config), [config]);
    const userPermissions = useSelector(selectUserPermissions);
    const hasAccessToUpdateDetails =
      hasAccess(editChargerDetailsAccessId, userPermissions) || hasAccess(viewChargerCustomer, userPermissions);

    const openModal = () => {
      togglePersonalizeModal(true);
    };

    const closeModal = () => {
      togglePersonalizeModal(false);
    };

    const personalizeColumnsHandleSubmit = (data) => {
      setColumnsConfig(data);
      togglePersonalizeModal(false);
    };

    const openDetailsUpdate = () => setIsOpenDetailsUpdateModal(true);
    const closeDetailsUpdate = () => setIsOpenDetailsUpdateModal(false);
    const onClickDetailsUpdateHandler = () => openDetailsUpdate();

    const pageLabelStyle = {
      display: 'flex',
      alignItems: 'center',
      fontWeight: 700,
    };

    const pageLabel = (
      <div style={pageLabelStyle}>
        <div className="title">
          <MappedLabel reserve="Charger Info">mainMenu/chargers/chargerInfo</MappedLabel>
        </div>
        <span className="toolbar-separator" />
      </div>
    );

    return (
      <Toolbar className="chargers-toolbar">
        <div className="toolbar-group">
          {pageLabel}
          <AdvancedSearch
            id={chargersInfoTableId}
            isShowAdvancedSearch={isShowAdvancedSearch}
            excludedFields={excludedFields}
            enabled={advancedSearchEnabled}
            getFields={getChargersAdvancedSearchConfig}
            onApply={onFilterApply}
            onToggle={onToggleAdvancedSearch}
          />
          <TransferButton disabled={selectedDataIds.length === 0 && selectAll === false} listId={chargersInfoTableId} />
          {hasAccessToUpdateDetails && <Button onClick={onClickDetailsUpdateHandler}>{DETAILS_UPDATE_BUTTON_TITLE}</Button>}
        </div>
        <div className="toolbar-group">
          <span className="toolbar-separator" />
          <IconButton size="large" onClick={openModal} style={isOnlyMobile ? columnsConfigButtonStyle : {}}>
            <SettingsIcon />
          </IconButton>
          <PersonalizeModal
            columnsConfig={columnsConfig}
            onOpen={openModal}
            onClose={closeModal}
            onSubmit={personalizeColumnsHandleSubmit}
            isOpen={personalizeModal}
          />
          <DetailsUpdateModal onClose={closeDetailsUpdate} isOpen={isOpenDetailsUpdateModal} />
        </div>
      </Toolbar>
    );
  },
);

export default ChargersListToolbar;
